import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { NavigationStateProvider } from './contexts/NavigationStateContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import OrdersList from './pages/OrdersList';
import OrderForm from './pages/OrderForm';
import OrderDetail from './pages/OrderDetail';
import MasterData from './pages/MasterData';
import SampleMaster from './pages/SampleMaster';
import FieldActivityDashboard from './pages/FieldActivityDashboard';
import FieldVisitForm from './pages/FieldVisitForm';
import FieldVisitDetail from './pages/FieldVisitDetail';
import FieldVisitGroupDetail from './pages/FieldVisitGroupDetail';
import FieldTasks from './pages/FieldTasks';
import FieldActivityReports from './pages/FieldActivityReports';
import RecycleBin from './pages/RecycleBin';
import Layout from './components/Layout';
import './App.css';

// Hide splash screen and show app content
const hideSplashScreen = () => {
  const splash = document.getElementById('splash-screen');
  
  if (splash) {
    // Hide splash immediately - no transition needed
    splash.style.display = 'none';
  }
};

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  // Hide splash screen once auth check is complete
  useEffect(() => {
    if (!loading) {
      hideSplashScreen();
    }
  }, [loading]);

  if (loading) {
    return null; // Splash screen is still visible
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  // Hide splash screen once auth check is complete
  useEffect(() => {
    if (!loading) {
      hideSplashScreen();
    }
  }, [loading]);

  if (loading) {
    return null; // Splash screen is still visible
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <OrdersList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/create"
        element={
          <ProtectedRoute>
            <OrderForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/edit/:orderId"
        element={
          <ProtectedRoute>
            <OrderForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <ProtectedRoute>
            <OrderDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-data"
        element={
          <ProtectedRoute>
            <MasterData />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recycle-bin"
        element={
          <ProtectedRoute>
            <RecycleBin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sample-master"
        element={
          <ProtectedRoute>
            <SampleMaster />
          </ProtectedRoute>
        }
      />
      {/* Field Activity Routes */}
      <Route
        path="/field-activity"
        element={
          <ProtectedRoute>
            <FieldActivityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/new-visit"
        element={
          <ProtectedRoute>
            <FieldVisitForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/edit/:visitId"
        element={
          <ProtectedRoute>
            <FieldVisitForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/visit/:visitId"
        element={
          <ProtectedRoute>
            <FieldVisitDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/group/:groupId"
        element={
          <ProtectedRoute>
            <FieldVisitGroupDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/tasks"
        element={
          <ProtectedRoute>
            <FieldTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/field-activity/reports"
        element={
          <ProtectedRoute>
            <FieldActivityReports />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <UnsavedChangesProvider>
          <NavigationStateProvider>
            <AppRoutes />
            <Toaster position="top-right" />
          </NavigationStateProvider>
        </UnsavedChangesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
